import { Box, Divider, Stack, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { VirtualItem } from "@tanstack/react-virtual";
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DashboardsContext } from "../../../contexts/DashboardsContext";
import { WorkspaceContext } from "../../../contexts/WorkspaceContext";
import { WorkspacesContext } from "../../../contexts/WorkspacesContext";
import useDashboardsTable from "../../../hooks/useDashboardsTable";
import useVirtualTable from "../../../hooks/useVirtualTable";
import useWorkspaces from "../../../hooks/useWorkspaces";
import { scrollbarStyle } from "../../../shared/dashboard";
import { Dashboard, Workspace } from "../../../types/files";
import { collectionKey } from "../../organisms/dashboards/DashboardsView";
import { FallbackLoading } from "../../templates/loader";
import NoDashboardsFoundView from "../dashboards-empty/NoDashboardsFoundView";
import DashboardsTableHeader from "../dashboards-table/DashboardsTableHeader";
import DashboardsTableRow from "../dashboards-table/DashboardsTableRow";

const useStyles = makeStyles((theme) => ({
    container: {
        height: 'calc(100% - 30px)',
        overflowY: 'auto',
        overflowX: 'hidden',
        marginTop: 20,
        ...scrollbarStyle,
    },
    headers: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        ...scrollbarStyle,
    },
    //
    table: {
        width: '100%',
        height: 'calc(100vh - 200px)',
        marginLeft: 12,
    },
    header: {
        width: '100%',
        height: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollBehavior: 'auto',
        borderBottom: `1px solid ${theme.colors.neutral['400']}`,
        ...scrollbarStyle,
    },
    rows: {
        position: 'relative',
        minWidth: '100%',
        width: 'fit-content',
        height: '100%',
        overflow: 'auto',
        scrollBehavior: 'smooth',
        ...scrollbarStyle,
    },
    row: {
        position: 'absolute',
        top: 0,
        left: 0,
        minWidth: '100%',
        width: 'fit-content',
        height: 'auto',
        borderTop: `1px solid ${theme.colors.neutral['400']}`,
        background: `#fff`,
        "&:hover": {
            background: `rgb(246, 251, 251, 0.8)`,
        },
    },
    section: {
        width: '100%',
        fontFamily: 'Inter',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: '#3ABDC6',
        "&::before, &::after": {
            borderTop: 'thin solid #3ABDC6',
        }
    },
    loadingText: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: 700,
        color: theme.colors.neutral['900'],
    },
    loadingTextSub: {
        fontFamily: 'Inter',
        fontSize: '0.9rem',
        fontWeight: 500,
        color: '#9e9e9e',
    },
    loading: {
        width: '100%',
        height: '100%',
        color: theme.palette.primary.main,
    },
    sharedTitle: {
        fontFamily: 'Inter',
        fontSize: '1.2rem',
        fontWeight: '600',
        color: theme.colors.neutral['700'],
    },
}));

export const TableHeaders = ['Name', 'Fit score', 'Stage', 'Sectors', 'Location', '', 'Added', ''];

const SharedByMe: React.FC<{
    workspace?: Workspace,
    search?: string,
 }> = ({ workspace, search }) => {
    const classes = useStyles();
    const { dashboards, isPublicView } = useContext(DashboardsContext);
    const { setWorkspaceScores } = useContext(WorkspacesContext);
    const { workspace: globalWorkspace } = useContext(WorkspaceContext);
    const { refreshScores, computeWorkspaceScores } = useWorkspaces();

    const [scoring, setScoring] = useState<boolean>(false);
    const timerRef = useRef<string | number | NodeJS.Timeout | undefined>(undefined);

    const localWorkspace = useMemo(() => workspace ?? globalWorkspace, [globalWorkspace, workspace]);

    const { loading, rows, filters, handleFilterRows } = useDashboardsTable(localWorkspace?.id ?? undefined, search);
    const { allRows, rowVirtualizer, hasNextPage, virtualRowsRef, virtualHeaderRef }
        = useVirtualTable({ key: 'shared-by-me-138', source: rows, limit: 20, itemHeight: 60 });

    const getMembership = useCallback((dashboard: Dashboard) =>
        dashboard?.workspaceMemberships?.find(wsMem => wsMem.workspaceId === localWorkspace?.id)
    , [localWorkspace?.id]);

    const handleRefresh = useCallback(() => {
        if (!!localWorkspace)
            refreshScores([localWorkspace], dashboards, () => setScoring(true))
    }, [localWorkspace, refreshScores, dashboards]);

    useEffect(() => {
        if (!!localWorkspace && scoring) {
            timerRef.current = setTimeout(() => {
               clearInterval(timerRef.current);
               computeWorkspaceScores([localWorkspace]).then((updatedWorkspaceScores) => {
                   setWorkspaceScores(updatedWorkspaceScores);
                }).finally(() => setScoring(false));
           }, 3000);
       }

       return () => {
           clearTimeout(timerRef.current);
       }
    // eslint-disable-next-line
    }, [scoring]);

    if (!localWorkspace)
        return (<></>);

    return (<>
        {loading ? (<FallbackLoading />) : (<>
            {isPublicView && (<>
                <Typography className={classes.sharedTitle} pl={1} py={1}>
                    {`Deals shared:`}
                </Typography>
                <Divider sx={{ width: '100%' }} />
            </>)}
            <Stack className={classes.table}>
                {loading ? (<FallbackLoading />) : (<>
                    <Stack alignItems="flex-start" justifyContent="flex-start" height="100%">
                        <Box width="100%" height="fit-content" overflow="hidden">
                            <Box className={classes.header} ref={virtualHeaderRef}>
                                <DashboardsTableHeader
                                    columns={TableHeaders}
                                    filters={filters}
                                    onFilter={handleFilterRows}
                                    onRefreshScores={() => handleRefresh()}
                                    actions={['Remove from workspace']}
                                    xAdjust={80}
                                    multiAction noStatus/>
                            </Box>
                        </Box>
                        {!!rows?.length ? (<>
                            <Box width="100%" height="100%" overflow="hidden">
                                <Box className={classes.rows} ref={virtualRowsRef}
                                    sx={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                                    {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => (
                                        <Box className={classes.row}
                                            sx={{  height: `${virtualRow.size}px`, transform: `translateY(${virtualRow.start}px)`, }}
                                            key={'shared-by-me-192-' + virtualRow.index} >
                                            {(virtualRow.index > allRows.length - 1) && hasNextPage
                                                ? (<FallbackLoading />)
                                                : (<>{!allRows[virtualRow.index] ? (
                                                    <Stack direction="row" alignItems="center" justifyContent="stretch" width="100%" minHeight="60px">
                                                        <Divider className={classes.section}>{'Previously shared'}</Divider>
                                                    </Stack>
                                                ) : (
                                                    <DashboardsTableRow
                                                        dashboard={allRows[virtualRow.index]}
                                                        workspace={workspace ?? undefined}
                                                        columns={TableHeaders}
                                                        collectionKey={collectionKey}
                                                        timestamp={getMembership(allRows[virtualRow.index])?.updatedAt}
                                                        refreshingScore={scoring}
                                                        isPublic noStatus />
                                                )}</>)}
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </>) : (
                            <Stack alignItems="flex-start" justifyContent="flex-start" width="100%" height="100%">
                                <Divider sx={{ width: '100%' }} />
                                <Stack className={classes.rows} alignItems="flex-start" justifyContent="flex-start">
                                   <NoDashboardsFoundView noDataYet={!dashboards.length} noCompaniesFound />
                                </Stack>
                            </Stack>
                        )}
                    </Stack>
                </>)}
            </Stack>
        </>)}
    </>);
}

export default memo(SharedByMe);